import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import {
  fetchListings,
  type Listing,
} from '../../features/marketplace/api';
import { useAuth } from '../../contexts/AuthContext';

export function MarketplaceBrowsePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<Record<string, number>>({});
  const [dragStart, setDragStart] = useState<{ x: number; listingId: string } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    loadListings();
  }, []);

  async function loadListings() {
    try {
      setLoading(true);
      const data = await fetchListings(100);
      // Only show approved listings in public catalog
      const approvedListings = (data || []).filter(listing => listing.status === 'approved');
      setListings(approvedListings);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  }

  function nextImage(listingId: string, maxIndex: number, e?: React.MouseEvent) {
    e?.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      [listingId]: ((prev[listingId] || 0) + 1) % maxIndex
    }));
  }

  function prevImage(listingId: string, maxIndex: number, e?: React.MouseEvent) {
    e?.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      [listingId]: ((prev[listingId] || 0) - 1 + maxIndex) % maxIndex
    }));
  }

  function handleDragStart(listingId: string, e: React.MouseEvent | React.TouchEvent) {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragStart({ x: clientX, listingId });
    setIsDragging(false);
  }

  function handleDragMove(e: React.MouseEvent | React.TouchEvent) {
    if (!dragStart) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = Math.abs(clientX - dragStart.x);

    if (diff > 5) {
      setIsDragging(true);
    }
  }

  function handleDragEnd(listing: Listing, e: React.MouseEvent | React.TouchEvent) {
    if (!dragStart || dragStart.listingId !== listing.id) {
      setDragStart(null);
      setIsDragging(false);
      return;
    }

    const clientX = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
    const diff = clientX - dragStart.x;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      // Was a swipe - change image and prevent click event
      e.stopPropagation();
      if (diff > 0) {
        prevImage(listing.id, listing.media.length);
      } else {
        nextImage(listing.id, listing.media.length);
      }
    }

    setDragStart(null);
    setIsDragging(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <div className="text-center text-slate-600 dark:text-slate-400 py-12">Loading listings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 p-4">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
          Error: {error}
          <Button onClick={loadListings} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Browse Listings</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Discover services with privacy-first aliases</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/marketplace/buyer')}
              variant="secondary"
            >
              My Bookings
            </Button>
            {user?.role === 'seller' && (
              <Button
                onClick={() => navigate('/marketplace/seller')}
                variant="secondary"
              >
                Seller Dashboard
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/marketplace/admin')}
                variant="secondary"
              >
                🛡️ Admin Panel
              </Button>
            )}
          </div>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 dark:text-slate-400 text-lg">No listings available yet</p>
            <p className="text-slate-600 dark:text-slate-500 text-sm mt-2">Check back soon or create your own listing!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map((listing) => {
              const imageIndex = currentImageIndex[listing.id] || 0;
              const hasMultipleImages = listing.media && listing.media.length > 1;

              return (
                <div
                  key={listing.id}
                  onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden cursor-pointer hover:border-brand-500 dark:hover:border-brand-600 transition-colors shadow-sm hover:shadow-md"
                >
                  {/* Image Carousel */}
                  {listing.media && listing.media.length > 0 ? (
                    <div
                      className="h-48 bg-slate-100 dark:bg-slate-900 flex items-center justify-center overflow-hidden relative group"
                      onMouseDown={(e) => handleDragStart(listing.id, e)}
                      onMouseMove={handleDragMove}
                      onMouseUp={(e) => handleDragEnd(listing, e)}
                      onMouseLeave={(e) => {
                        if (dragStart) handleDragEnd(listing, e);
                      }}
                      onTouchStart={(e) => handleDragStart(listing.id, e)}
                      onTouchMove={handleDragMove}
                      onTouchEnd={(e) => handleDragEnd(listing, e)}
                    >
                      <img
                        src={listing.media[imageIndex]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                        draggable={false}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<div class="text-slate-600 text-sm">Image unavailable</div>';
                        }}
                      />

                      {/* Navigation Arrows */}
                      {hasMultipleImages && (
                        <>
                          <button
                            onClick={(e) => prevImage(listing.id, listing.media.length, e)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => nextImage(listing.id, listing.media.length, e)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}

                      {/* Dots Indicator */}
                      {hasMultipleImages && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {listing.media.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentImageIndex(prev => ({ ...prev, [listing.id]: idx }));
                              }}
                              className={`w-2 h-2 rounded-full transition-all ${
                                idx === imageIndex
                                  ? 'bg-white w-4'
                                  : 'bg-white/50 hover:bg-white/70'
                              }`}
                            />
                          ))}
                        </div>
                      )}

                      {/* Image Counter */}
                      {hasMultipleImages && (
                        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          {imageIndex + 1} / {listing.media.length}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                      <svg className="w-16 h-16 text-slate-400 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{listing.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">{listing.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-bold text-brand-600 dark:text-brand-400">
                        {listing.currency} {(listing.priceCents / 100).toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-500">by {listing.sellerAlias}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
